import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { RefreshCw } from "lucide-react";
import { api } from "@/lib/api";
import { freshnessLabel, painLabel, platformLabel } from "@/lib/commentLabels";
import CommentsPageShell from "@/components/comments/CommentsPageShell";
import DailyTrendChart from "@/components/DailyTrendChart";
import StatsExplainer from "@/components/StatsExplainer";
import { Button } from "@/components/ui/button";
import { InfoTip } from "@/components/ui/tooltip";
import { cn, formatNumber } from "@/lib/utils";

export default function KomentarzeStatystyki() {
  const qc = useQueryClient();
  const [window, setWindow] = useState("30d");

  const { data: statsData } = useQuery({
    queryKey: ["comments-stats", { window, is_reply: false }],
    queryFn: () => api.commentsStats({ window, is_reply: false }),
  });
  const { data: freshnessData } = useQuery({
    queryKey: ["comments-freshness"],
    queryFn: api.commentsFreshness,
  });
  const { data: coverageData } = useQuery({
    queryKey: ["comments-coverage"],
    queryFn: () => api.commentsCoverage(30),
  });
  const { data: digestData } = useQuery({
    queryKey: ["comments-digest"],
    queryFn: api.commentsDigest,
  });

  const refresh = useMutation({
    mutationFn: api.commentsRefresh,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["comments-facets"] });
      qc.invalidateQueries({ queryKey: ["comments-stats"] });
      qc.invalidateQueries({ queryKey: ["comments-list"] });
      qc.invalidateQueries({ queryKey: ["comments-posts"] });
      qc.invalidateQueries({ queryKey: ["comments-freshness"] });
      qc.invalidateQueries({ queryKey: ["comments-coverage"] });
      qc.invalidateQueries({ queryKey: ["comments-digest"] });
    },
  });

  const stats = statsData?.payload;
  const freshness = freshnessData?.payload;
  const coverage = coverageData?.payload;
  const digestWindows = digestData?.payload?.digest?.windows || {};
  const digestTop = [
    ...((digestWindows.last_24h?.top_comments || []).map((c) => ({ ...c, win: "24h" }))),
    ...((digestWindows.last_7d?.top_comments || []).slice(0, 5).map((c) => ({ ...c, win: "7d" }))),
  ].slice(0, 8);

  const daily = (stats?.daily || []).map((d) => ({
    date: d.date,
    instagram: Number(d.instagram || 0),
    facebook: Number(d.facebook || 0),
    youtube: Number(d.youtube || 0),
    tiktok: Number(d.tiktok || 0),
  }));

  return (
    <CommentsPageShell
      tab="stats"
      total={freshness?.live_total}
      totalWord="w plikach"
      actions={
        <Button size="sm" variant="brand" onClick={() => refresh.mutate()} disabled={refresh.isPending}>
          <RefreshCw className={cn("mr-2 h-3.5 w-3.5", refresh.isPending && "animate-spin")} />
          Odśwież snapshot
        </Button>
      }
    >
      <div className="mb-4 flex flex-wrap gap-2">
        {(freshness?.platforms || []).map((p) => (
          <span
            key={p.id}
            className={cn(
              "inline-flex rounded-full border px-2.5 py-0.5 text-xs font-medium",
              p.status === "live"
                ? "border-emerald-200 bg-emerald-50 text-emerald-800"
                : "border-amber-200 bg-amber-50 text-amber-800",
            )}
          >
            {platformLabel(p.id)} · {freshnessLabel(p.status)}
            {p.age_hours != null ? ` · ${Math.round(p.age_hours)}h` : ""}
          </span>
        ))}
      </div>

      <StatsExplainer variant="komentarze" className="mb-5" />

      <div className="mb-4 flex flex-wrap gap-2">
        {[
          ["24h", "24 godziny"],
          ["7d", "7 dni"],
          ["30d", "30 dni"],
          ["all", "Całość"],
        ].map(([id, label]) => (
          <Button
            key={id}
            size="sm"
            variant={window === id ? "default" : "outline"}
            onClick={() => setWindow(id)}
          >
            {label}
          </Button>
        ))}
      </div>

      <div className="mb-5 grid gap-3 sm:grid-cols-2 xl:grid-cols-6">
        <Kpi title="W filtrze" value={stats?.total ?? 0} tipId="comments_total" />
        <Kpi title="7 dni" value={stats?.last_7d ?? 0} tipId="comments_7d" />
        <Kpi
          title="CTA Mama"
          value={`${Math.round((stats?.cta_pct || 0) * 100)}%`}
          sub={`${stats?.cta ?? 0} haseł`}
          tipId="comments_cta"
        />
        <Kpi title="Historie" value={stats?.historia ?? 0} tipId="comments_historia" />
        <Kpi title="Pytania" value={stats?.pytanie ?? 0} tipId="comments_pytanie" />
        <Kpi title="Do akcji" value={stats?.action ?? 0} tipId="comments_action" />
      </div>

      <DailyTrendChart
        title="Wolumen 30 dni"
        subtitle="Komentarze z datą, czas PL. TikTok często ma datę fetchu, nie publikacji."
        data={daily}
        series={[
          { key: "instagram", label: "Instagram", color: "#c2410c", stackId: "p" },
          { key: "facebook", label: "Facebook", color: "#1d4ed8", stackId: "p" },
          { key: "youtube", label: "YouTube", color: "#b91c1c", stackId: "p" },
          { key: "tiktok", label: "TikTok", color: "#0f172a", stackId: "p" },
        ]}
        className="mb-5"
      />

      <div className="mb-5 grid gap-5 lg:grid-cols-2">
        <BarCard title="Klasy" rows={(stats?.classes || []).map((r) => ({ label: r.label, count: r.count }))} />
        <BarCard
          title="Bóle B1-B19"
          rows={(stats?.pains || []).map((r) => ({
            label: painLabel(r.id, r.label),
            count: r.count,
          }))}
        />
      </div>

      <div className="grid gap-5 lg:grid-cols-2">
        <div className="rounded-xl border border-slate-200 bg-white p-4">
          <h2 className="text-sm font-semibold text-slate-900">Pokrycie zapisu</h2>
          <div className="mt-3 space-y-3">
            {(coverage?.items || []).map((v) => (
              <div key={v.platform} className="flex justify-between text-sm">
                <span className="text-slate-700">{platformLabel(v.platform)}</span>
                <span className="font-mono text-xs text-slate-500">
                  {v.days_with_comments} dni / {v.comments_in_range}
                </span>
              </div>
            ))}
            <p className="text-xs text-slate-500">
              Reply ownera: {Math.round((coverage?.owner_reply_pct || 0) * 100)}% postów z 30 dni (
              {coverage?.owner_reply_posts ?? 0}/{coverage?.total_posts ?? 0}).
            </p>
            {(coverage?.quality_flags || []).slice(0, 3).map((f) => (
              <p key={f} className="text-xs text-amber-700">
                {f.replace(/\u2014/g, " - ")}
              </p>
            ))}
          </div>
        </div>
        <div className="rounded-xl border border-slate-200 bg-white p-4">
          <h2 className="text-sm font-semibold text-slate-900">Digest</h2>
          <div className="mt-3 space-y-2">
            {digestTop.map((c, i) => (
              <p key={`${c.win}-${i}`} className="text-xs text-slate-500">
                <span className="font-medium text-slate-800">{c.win}</span>{" "}
                {String(c.content || "").slice(0, 140)}
              </p>
            ))}
            {!digestTop.length && (
              <p className="text-sm text-slate-500">
                Brak precompute. Odśwież snapshot, żeby policzyć digest.
              </p>
            )}
          </div>
        </div>
      </div>
    </CommentsPageShell>
  );
}

function Kpi({
  title,
  value,
  sub,
  tipId,
}: {
  title: string;
  value: number | string;
  sub?: string;
  tipId: "comments_total" | "comments_7d" | "comments_cta" | "comments_historia" | "comments_pytanie" | "comments_action";
}) {
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-4">
      <div className="flex items-center gap-1 text-xs text-slate-500">
        {title}
        <InfoTip tipId={tipId} />
      </div>
      <div className="mt-1 text-2xl font-semibold tabular-nums text-slate-900">
        {typeof value === "number" ? formatNumber(value) : value}
      </div>
      {sub && <div className="text-xs text-slate-500">{sub}</div>}
    </div>
  );
}

function BarCard({ title, rows }: { title: string; rows: { label: string; count: number }[] }) {
  const max = Math.max(1, ...rows.map((r) => r.count));
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-4">
      <h2 className="text-sm font-semibold text-slate-900">{title}</h2>
      <div className="mt-3 space-y-2">
        {rows.slice(0, 8).map((r) => (
          <div key={r.label}>
            <div className="flex justify-between text-xs">
              <span className="truncate text-slate-700">{r.label}</span>
              <span className="tabular-nums text-slate-400">{r.count}</span>
            </div>
            <div className="mt-1 h-1.5 overflow-hidden rounded-full bg-slate-100">
              <div className="h-full rounded-full bg-brand" style={{ width: `${Math.round((r.count / max) * 100)}%` }} />
            </div>
          </div>
        ))}
        {!rows.length && <p className="text-sm text-slate-500">Brak agregatów.</p>}
      </div>
    </div>
  );
}
